// The App builder asks the owner for an outside host, a secret, an App event or schedule, a function's row
// writes or a change to its tables
// and then ends its turn. Once the owner has decided every such card in this chat (in this page session),
// the chat continues on its own with one short message, so the builder can run the function and report.
// The decision itself reaches the model through the action receipts already in the transcript.
import type { ActionView } from './action-confirmation';

export const APP_BUILD_DECISION_TOOLS: ReadonlySet<string> = new Set([
  'approve_app_outside_access',
  'apply_app_table_change',
  'set_app_data_writes',
  'set_app_event',
  'set_app_schedule',
  'set_app_connected_write',
  'confirm_app_code',
  'run_app_function_with_changes',
]);

export type AppBuildOutcome = 'approved' | 'declined' | 'failed';
type Decision = { key: string; outcome: AppBuildOutcome };

const decided = new Map<string, Decision[]>();
const listeners = new Set<() => void>();
const changed = () => listeners.forEach((listener) => listener());

function note(threadId: string, key: string, outcome: AppBuildOutcome) {
  const list = decided.get(threadId) ?? [];
  if (list.some((entry) => entry.key === key)) return;
  decided.set(threadId, [...list, { key, outcome }]);
  changed();
}

/** The person's own decision on an App builder card, from its approve or reject response. */
export function noteAppBuildDecision(action: ActionView) {
  if (!APP_BUILD_DECISION_TOOLS.has(action.tool)) return;
  const outcome: AppBuildOutcome | null =
    action.status === 'executed'
      ? 'approved'
      : action.status === 'rejected'
        ? 'declined'
        : action.status === 'failed'
          ? 'failed'
          : null;
  if (outcome) note(action.threadId, 'action:' + action.id, outcome);
}

/** A secret the owner saved in the secure field (request_app_secret). */
export function noteAppSecretSaved(threadId: string, appId: string, name: string) {
  note(threadId, `secret:${appId}:${name}`, 'approved');
}

export function subscribeAppBuildDecisions(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export const hasAppBuildDecisions = (threadId: string) => (decided.get(threadId)?.length ?? 0) > 0;

/** The decisions to continue with, taken once. */
export function takeAppBuildDecisions(threadId: string): AppBuildOutcome[] {
  const list = decided.get(threadId) ?? [];
  decided.delete(threadId);
  return list.map((entry) => entry.outcome);
}

export type AppBuildContinueCopy = {
  continueApproved: string;
  continueDeclined: string;
  continueMixed: string;
};

/** The one message the chat continues with. */
export function appBuildContinuation(outcomes: AppBuildOutcome[], copy: AppBuildContinueCopy) {
  if (outcomes.every((outcome) => outcome === 'approved')) return copy.continueApproved;
  if (outcomes.every((outcome) => outcome === 'declined')) return copy.continueDeclined;
  return copy.continueMixed;
}
